// Port of webapp/src/blocks/boardView.ts

import {Block, createBlock} from './block'
import {FilterGroup, createFilterGroup} from './filterGroup'

export type IViewType = 'board' | 'table' | 'gallery' | 'calendar'

export type ISortOption = { propertyId: '__title' | string, reversed: boolean }

export type KanbanCalculationFields = {
    calculation: string
    propertyId: string
}

export type BoardViewFields = {
    viewType: IViewType
    groupById?: string
    dateDisplayPropertyId?: string
    sortOptions: ISortOption[]
    visiblePropertyIds: string[]
    visibleOptionIds: string[]
    hiddenOptionIds: string[]
    collapsedOptionIds: string[]
    filter: FilterGroup
    cardOrder: string[]
    columnWidths: Record<string, number>
    columnCalculations: Record<string, string>
    kanbanCalculations: Record<string, KanbanCalculationFields>
    defaultTemplateId: string
}

export type BoardView = Block & {
    fields: BoardViewFields
}

export function createBoardView(block?: Partial<Block>): BoardView {
    return {
        ...createBlock(block),
        type: 'view',
        fields: {
            viewType: block?.fields?.viewType || 'board',
            groupById: block?.fields?.groupById,
            dateDisplayPropertyId: block?.fields?.dateDisplayPropertyId,
            sortOptions: block?.fields?.sortOptions?.map((o: ISortOption) => ({...o})) || [],
            visiblePropertyIds: block?.fields?.visiblePropertyIds?.slice() || [],
            visibleOptionIds: block?.fields?.visibleOptionIds?.slice() || [],
            hiddenOptionIds: block?.fields?.hiddenOptionIds?.slice() || [],
            collapsedOptionIds: block?.fields?.collapsedOptionIds?.slice() || [],
            filter: createFilterGroup(block?.fields?.filter),
            cardOrder: block?.fields?.cardOrder?.slice() || [],
            columnWidths: {...(block?.fields?.columnWidths || {})},
            columnCalculations: {...(block?.fields?.columnCalculations || {})},
            kanbanCalculations: {...(block?.fields?.kanbanCalculations || {})},
            defaultTemplateId: block?.fields?.defaultTemplateId || '',
        },
    }
}

/** Upstream strips leading emoji so alphabetical sorting stays intuitive. */
export function sortBoardViewsAlphabetically(views: BoardView[]): BoardView[] {
    return views.
        map((v) => ({view: v, title: v.title.replace(/^\p{Emoji}*\s*/u, '')})).
        sort((v1, v2) => v1.title.localeCompare(v2.title)).
        map((v) => v.view)
}

export const viewTypes: Array<{type: IViewType, label: string, icon: string}> = [
    {type: 'board', label: '看板', icon: 'viewBoard'},
    {type: 'table', label: '表格', icon: 'TableView'},
    {type: 'gallery', label: '画廊', icon: 'gallery'},
    {type: 'calendar', label: '日历', icon: 'calendar'},
]

/** The title a freshly created view gets; also what the seed data uses. */
export function viewTitleOf(viewType: IViewType): string {
    const found = viewTypes.find((v) => v.type === viewType)
    return `${found ? found.label : viewType}视图`
}
